import { apiRoutes } from '@/app/apiConfig/apiRoutes';
import {
  Company,
  CompanyDetails,
  CompanyImportMapping,
  CreateCompanyResponseResult,
  CreateReferralCompanyPayload,
  CreateCompanyImportMappingPayload,
  CreateCompanyImportMappingsResponseResult,
} from '@/app/shared-components/Types/Company';
import { Paged } from '../shared-components/Types/Paged';
import { apiSlice } from './apiSlice';
import { transformPayloadToFormData } from '@/utils/formatters';
import {
  MRT_ColumnFiltersState,
  MRT_PaginationState,
  MRT_SortingState,
} from 'material-react-table';
import { createMyUtiliesURL } from '@/utils/helpers';
import { Schema } from '@/app/main/send-invite/components/config';
import { User } from '../shared-components/Types/Users';

const imageFields = ['logo', 'w9', 'agreements'];

export const companiesApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<
      Paged<Company>,
      {
        filters: MRT_ColumnFiltersState;
        sorting: MRT_SortingState;
        pagination: MRT_PaginationState;
      }
    >({
      query: ({ filters, sorting, pagination }) => {
        const urlParams = createMyUtiliesURL(filters, sorting);

        return {
          url: apiRoutes.companies,
          method: 'GET',
          params: {
            ...Object.fromEntries(urlParams),
            page: pagination.pageIndex + 1,
            pageSize: pagination.pageSize,
          },
        };
      },
      providesTags: ['Companies'],
    }),
    getAllCompanies: builder.query<Paged<Company>, void>({
      query: () => {
        return {
          url: apiRoutes.companies,
          method: 'GET',
        };
      },
    }),
    getUsersWhoseCompaniesHaveImportMappings: builder.query<Paged<User>, void>({
      query: () => {
        return {
          url: `${apiRoutes.users}/importmappings`,
          method: 'GET',
          params: {
            pageSize: 10000,
          },
        };
      },
    }),
    getCompaniesByNameParameter: builder.query<
      Paged<Company>,
      { search: string; getOnlyReferralCompanies?: boolean; licenseeId?: number | null }
    >({
      query: ({
        search,
        getOnlyReferralCompanies,
        licenseeId,
      }: {
        search: string;
        getOnlyReferralCompanies?: boolean;
        licenseeId?: number | null;
      }) => {
        const filterSign = '@=*';
        const parameter = 'name';
        const baseUrl = new URL(import.meta.env.VITE_API_URL);
        const onlyReferralCompaniesFilter = getOnlyReferralCompanies
          ? 'parentCompanyId==null,'
          : '';
        const licenseeFilter = licenseeId ? `licenseeId==${licenseeId},` : '';
        baseUrl.searchParams.append(
          'Filters',
          `${onlyReferralCompaniesFilter}${licenseeFilter}${parameter}${filterSign}${search}`,
        );
        const url = `${apiRoutes.companies}${baseUrl.search}`;
        return {
          url,
          method: 'GET',
        };
      },
    }),
    getCompaniesByIds: builder.query<Paged<Company>, string>({
      query: (search: string) => {
        const url = `${apiRoutes.companies}?filters=id==${search}`;
        return {
          url,
          method: 'GET',
        };
      },
    }),
    getCompany: builder.query<CompanyDetails, string | number | undefined>({
      // TODO: Change to UserResponseResult
      query: (id: string | undefined) => {
        if (id) {
          return { url: `${apiRoutes.companies}/${id}`, method: 'GET' };
        }
        throw new Error('Company id is not defined');
      },
      providesTags: (_result, _error, id) => [{ type: 'Company', id }],
    }),
    getInitialTextForCompany: builder.query<
      { initialText: string; initialTextAfterHours: string },
      { companyId: string | undefined; licenseeId: number }
    >({
      query: ({ companyId, licenseeId }) => {
        return {
          url: apiRoutes.companyInitialText,
          method: 'GET',
          params: { refCompanyId: companyId, licenseeId },
        };
      },
    }),
    createCompany: builder.mutation<CreateCompanyResponseResult, CreateReferralCompanyPayload>({
      query: ({ ...patch }) => {
        return {
          url: apiRoutes.companies,
          method: 'POST',
          data: transformPayloadToFormData(patch, imageFields),
        };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['Companies']),
      transformResponse: (_response: any, meta: any) => {
        return {
          id: meta,
        };
      },
    }),
    createInitialTextForCompany: builder.mutation<
      void,
      {
        data: {
          refCompanyId: number;
          licenseeId: number;
          initialText?: string | null;
          initialTextAfterHours?: string | null;
        };
      }
    >({
      query: ({ data }) => {
        return {
          url: apiRoutes.companyInitialText,
          method: 'POST',
          data,
        };
      },
    }),
    updateCompany: builder.mutation<Company, { id: string; company: CreateReferralCompanyPayload }>(
      {
        query: ({ id, company }) => {
          return {
            url: `${apiRoutes.companies}/${id}`,
            method: 'PUT',
            data: transformPayloadToFormData(company, imageFields),
          };
        },
        invalidatesTags: (_result, _error, { id }) => [{ type: 'Company', id }, 'Companies'],
      },
    ),
    deleteCompany: builder.mutation<void, number>({
      query: (id) => {
        return {
          url: `${apiRoutes.companies}/${id}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error) => (error ? [] : ['Companies']),
    }),
    // invitations
    inviteUser: builder.mutation<void, { companyId: number; user: Schema }>({
      query: ({ companyId, user }) => {
        return {
          url: `${apiRoutes.companies}/${companyId}/invite`,
          method: 'POST',
          data: user,
        };
      },
      invalidatesTags: (result, error) => (error ? [] : ['Invites']),
    }),

    // #region CompanyImportMappings

    createCompanyImportMappings: builder.mutation<
      CreateCompanyImportMappingsResponseResult,
      CreateCompanyImportMappingPayload
    >({
      query: ({ ...mapping }) => {
        return {
          url: apiRoutes.companyImportMapping,
          method: 'POST',
          data: mapping,
        };
      },
      invalidatesTags: (_result, error) => (error ? [] : ['Companies']),
      transformResponse: (_response: any, meta: any) => {
        return {
          id: meta,
        };
      },
    }),
    updateCompanyImportMappings: builder.mutation<
      CompanyImportMapping,
      { importId: string; mapping: CreateCompanyImportMappingPayload }
    >({
      query: ({ importId, mapping }) => {
        return {
          url: `${apiRoutes.companyImportMapping}/${importId}`,
          method: 'PUT',
          data: mapping,
        };
      },
      invalidatesTags: [{ type: 'CompanyImportMapping', id: 'LIST' }],
    }),
    getCompanyImportMapping: builder.query<CompanyImportMapping, string | number | undefined>({
      query: (id) => ({
        url: `${apiRoutes.companyImportMapping}/getbycompanyid/${id}`,
        method: 'GET',
      }),
      providesTags: () => [{ type: 'CompanyImportMapping', id: 'LIST' }],
    }),

    // #endregion
  }),

  overrideExisting: false,
});

export const {
  useGetCompanyImportMappingQuery,
  useGetCompaniesQuery,
  useGetAllCompaniesQuery,
  useLazyGetCompaniesByNameParameterQuery,
  useLazyGetCompaniesByIdsQuery,
  useCreateCompanyMutation,
  useCreateInitialTextForCompanyMutation,
  useGetCompanyQuery,
  useGetInitialTextForCompanyQuery,
  useUpdateCompanyMutation,
  useDeleteCompanyMutation,
  useInviteUserMutation,
  useUpdateCompanyImportMappingsMutation,
  useCreateCompanyImportMappingsMutation,
} = companiesApiSlice;
